import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { Download, RefreshCw, Trash2, Upload, UserPlus } from "lucide-react"
import { Avatar } from "@ui/Avatar"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Combobox } from "@ui/Combobox"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { Pill } from "@ui/Pill"
import { Select } from "@ui/Select"
import { Textarea } from "@ui/Textarea"
import { toast } from "sonner"
import type {
  Folder,
  GrantableInvitation,
  GrantableMember,
  Project,
  ProjectInvitation,
  ProjectMember,
} from "@lib/inertia-types"
import { ImportCollectionDialog, type ImportFormat } from "@shared/ImportCollectionDialog"

export default function ProjectSettings({
  project,
  can_manage,
  can_manage_access,
  access_members,
  access_invitations,
  grantable_members,
  grantable_invitations,
  folders,
}: {
  project: Project & { mock_token: string }
  can_manage: boolean
  can_manage_access: boolean
  access_members: ProjectMember[]
  access_invitations: ProjectInvitation[]
  grantable_members: GrantableMember[]
  grantable_invitations: GrantableInvitation[]
  folders: Folder[]
}) {
  return (
    <>
      <Head title={`Settings · ${project.name} · Fetch`} />
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="mb-6 text-title3 font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
          Project settings
        </h1>

        <div className="flex flex-col gap-6">
          <DetailsCard project={project} disabled={!can_manage} />
          <MockTokenCard project={project} disabled={!can_manage} />
          <ImportExportCard project={project} folders={folders} disabled={!can_manage} />
          <ProjectAccessCard
            project={project}
            members={access_members}
            invitations={access_invitations}
            grantable={grantable_members}
            grantableInvitations={grantable_invitations}
            canManage={can_manage_access}
          />
          {can_manage && <DangerCard project={project} />}
        </div>
      </div>
    </>
  )
}

function DetailsCard({ project, disabled }: { project: Project; disabled: boolean }) {
  const [name, setName] = React.useState(project.name)
  const [description, setDescription] = React.useState(project.description ?? "")

  function save() {
    router.patch(`/projects/${project.slug}`, { name, description }, { preserveScroll: true })
  }

  return (
    <Card padding="lg">
      <h2 className="mb-4 text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">Details</h2>
      <div className="flex flex-col gap-4">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} disabled={disabled} />
        </Field>
        {!disabled && (
          <div className="flex justify-end">
            <Button onClick={save}>Save</Button>
          </div>
        )}
      </div>
    </Card>
  )
}

function MockTokenCard({ project, disabled }: { project: Project & { mock_token: string }; disabled: boolean }) {
  function regenerate() {
    if (!confirm("Regenerate the mock token? Existing mock URLs will stop working immediately.")) return
    router.post(`/projects/${project.slug}/regenerate_mock_token`, {}, { preserveScroll: true })
  }

  return (
    <Card padding="lg">
      <h2 className="mb-1 text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
        Mock token
      </h2>
      <p className="mb-3 text-mini text-cloverhound-gray-600">
        The unguessable token in every mock URL for this project. Rotate it to revoke all current mock URLs.
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded-md bg-cloverhound-gray-100 px-3 py-2 font-mono text-mini text-cloverhound-gray-700 dark:bg-cloverhound-gray-850 dark:text-cloverhound-gray-300">
          {project.mock_token}
        </code>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(project.mock_token)
            toast.success("Copied")
          }}
        >
          Copy
        </Button>
        {!disabled && (
          <Button variant="outline" size="sm" onClick={regenerate}>
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Regenerate
          </Button>
        )}
      </div>
    </Card>
  )
}

function ImportExportCard({ project, folders, disabled }: { project: Project; folders: Folder[]; disabled: boolean }) {
  const [format, setFormat] = React.useState<ImportFormat>("postman")
  const [importOpen, setImportOpen] = React.useState(false)

  return (
    <Card padding="lg">
      <h2 className="mb-1 text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
        Import &amp; export
      </h2>
      <p className="mb-3 text-mini text-cloverhound-gray-600">
        Postman Collection v2.1 (JSON) or Bruno (.zip). Exports never include credential values.
      </p>
      <div className="flex items-center gap-3">
        <div className="w-40">
          <Select.Root value={format} onValueChange={(v) => v && setFormat(v)}>
            <Select.Trigger aria-label="Format">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="postman">Postman v2.1</Select.Item>
              <Select.Item value="bruno">Bruno</Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
        {!disabled && (
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            <Upload className="size-4" aria-hidden="true" />
            Import
          </Button>
        )}
        <a href={`/projects/${project.slug}/export?format=${format}`}>
          <Button variant="secondary">
            <Download className="size-4" aria-hidden="true" />
            Export
          </Button>
        </a>
      </div>
      <ImportCollectionDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        project={project}
        folders={folders}
        initialFormat={format}
        reloadOnly={["folders", "flash"]}
      />
    </Card>
  )
}

function ProjectAccessCard({
  project,
  members,
  invitations,
  grantable,
  grantableInvitations,
  canManage,
}: {
  project: Project
  members: ProjectMember[]
  invitations: ProjectInvitation[]
  grantable: GrantableMember[]
  grantableInvitations: GrantableInvitation[]
  canManage: boolean
}) {
  const [selectedTarget, setSelectedTarget] = React.useState("")
  const accessOnly = {
    preserveScroll: true,
    only: ["access_members", "access_invitations", "grantable_members", "grantable_invitations", "flash"],
  }

  function grant() {
    const [targetType, targetId] = selectedTarget.split(":")
    if (!targetId) return

    const assigningInvitation = targetType === "invitation"
    router.post(
      `/projects/${project.slug}/${assigningInvitation ? "invitations" : "members"}`,
      assigningInvitation ? { invitation_id: targetId } : { user_id: targetId },
      { ...accessOnly, onFinish: () => setSelectedTarget("") },
    )
  }

  function revoke(member: ProjectMember) {
    if (!confirm(`Remove ${member.user.email_address}'s access to this project?`)) return
    router.delete(`/projects/${project.slug}/members/${member.id}`, accessOnly)
  }

  function revokeInvitation(invitation: ProjectInvitation) {
    if (!confirm(`Remove ${invitation.email}'s invited access to this project?`)) return
    router.delete(`/projects/${project.slug}/invitations/${invitation.id}`, accessOnly)
  }

  const grantableOptions = [
    ...grantable.map((member) => ({
      value: `user:${member.id}`,
      label: member.name ? `${member.name} (${member.email_address})` : member.email_address,
      keywords: [member.email_address],
    })),
    ...grantableInvitations.map((invitation) => ({
      value: `invitation:${invitation.id}`,
      label: `${invitation.email} — invited`,
      keywords: [invitation.email, "invited", "pending"],
    })),
  ]

  return (
    <Card padding="lg">
      <h2 className="mb-1 text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
        Project access
      </h2>
      <p className="mb-3 text-mini text-cloverhound-gray-600">
        Organization admins and owners always have access. Grant project access to other members or invitees here.
      </p>

      {canManage && (
        <div className="mb-4 flex items-center gap-2">
          <div className="flex-1">
            <Combobox
              options={grantableOptions}
              value={selectedTarget}
              onChange={setSelectedTarget}
              placeholder="Add a member or invitee…"
              searchPlaceholder="Search people…"
              emptyMessage="No one to add"
            />
          </div>
          <Button onClick={grant} disabled={!selectedTarget}>
            <UserPlus className="size-4" aria-hidden="true" />
            Grant access
          </Button>
        </div>
      )}

      {members.length === 0 && invitations.length === 0 ? (
        <p className="text-mini text-cloverhound-gray-500">
          No members or invitees have been granted access yet.
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {members.map((member) => (
            <div key={member.id} className="flex items-center gap-3 py-1.5">
              <Avatar
                size="md"
                alt={member.user.name ?? member.user.email_address}
                src={member.user.avatar_url ?? undefined}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-small font-medium text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
                  {member.user.name ?? member.user.email_address}
                </p>
                <p className="truncate text-mini text-cloverhound-gray-600">{member.user.email_address}</p>
              </div>
              {member.org_role && member.org_role !== "member" && <Pill>{member.org_role}</Pill>}
              {canManage && (
                <Button
                  variant="ghost"
                  size="iconXs"
                  onClick={() => revoke(member)}
                  aria-label="Remove access"
                >
                  <Trash2 className="size-3.5" aria-hidden="true" />
                </Button>
              )}
            </div>
          ))}
          {invitations.map((invitation) => (
            <div key={`invitation-${invitation.id}`} className="flex items-center gap-3 py-1.5">
              <Avatar size="md" alt={invitation.email} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-small font-medium text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
                  {invitation.email}
                </p>
                <p className="truncate text-mini text-cloverhound-gray-600">
                  Invited as {invitation.org_role}
                </p>
              </div>
              <Pill variant={invitation.status === "expired" ? "danger" : "accent"}>
                {invitation.status === "expired" ? "Expired" : "Pending"}
              </Pill>
              {canManage && (
                <Button
                  variant="ghost"
                  size="iconXs"
                  onClick={() => revokeInvitation(invitation)}
                  aria-label={`Remove ${invitation.email}'s invited access`}
                >
                  <Trash2 className="size-3.5" aria-hidden="true" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function DangerCard({ project }: { project: Project }) {
  function destroy() {
    if (!confirm(`Delete project "${project.name}"? This removes all its requests, mocks, and logs.`)) return
    router.delete(`/projects/${project.slug}`)
  }

  return (
    <Card padding="lg" className="border-cloverhound-red-500/30">
      <h2 className="mb-1 text-small font-semibold text-cloverhound-red-600 dark:text-cloverhound-red-400">
        Danger zone
      </h2>
      <p className="mb-3 text-mini text-cloverhound-gray-600">
        Deleting a project is permanent.
      </p>
      <Button variant="destructive" onClick={destroy}>
        Delete project
      </Button>
    </Card>
  )
}
